import { useEffect, useState } from "react";
import api from "@/lib/api";

function toEmbed(url) {
  if (!url) return null;
  const yt = url.match(
    /(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/))([\w-]+)/,
  );
  if (yt) return `https://www.youtube.com/embed/${yt[1]}`;
  const vimeo = url.match(/vimeo\.com\/(\d+)/);
  if (vimeo) return `https://player.vimeo.com/video/${vimeo[1]}`;
  return /^https?:\/\//i.test(url) ? url : null;
}

function safeUrl(url, fallback = "#") {
  if (!url) return fallback;
  return /^(https?:\/\/|mailto:|\/|#)/i.test(url) ? url : fallback;
}
function buttonUrl(block) {
  const value = block.url || block.link?.value || "";
  const type = block.linkType || block.link?.type;
  if (type === "email" && value && !value.startsWith("mailto:")) return `mailto:${value}`;
  if (type === "phone" && value && !value.startsWith("tel:")) return `tel:${value}`;
  if (type === "anchor" && value && !value.startsWith("#")) return `#${value}`;
  return safeUrl(value);
}
function buttonClass(block) {
  const size = block.size === "small" ? "px-4 py-2 text-xs" : block.size === "large" ? "px-8 py-4 text-base" : "px-6 py-3 text-sm";
  const style = block.style === "outline" ? "border border-dir-gold text-dir-gold" : block.style === "text" ? "text-dir-gold" : block.style === "secondary" ? "bg-dir-elevated text-dir-text" : "bg-gradient-to-r from-dir-warm to-dir-gold text-dir-deep";
  return `inline-flex items-center rounded-full font-bold transition-[filter,background-color,border-color,color] hover:brightness-110 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-dir-gold ${size} ${style}`;
}

function ResponsiveBlock({ block, children }) {
  const base = block.settings || {};
  const rules = ["desktop", "tablet", "mobile"].map((device) => block.responsive_settings?.[device] || {});
  const id = `builder-block-${String(block.id || Math.random()).replace(/[^a-z0-9_-]/gi, "")}`;
  const css = (value) => [
    value.visible === false ? "display:none" : "",
    value.paddingTop != null ? `padding-top:${value.paddingTop}px` : "",
    value.paddingBottom != null ? `padding-bottom:${value.paddingBottom}px` : "",
    value.fontSize != null ? `font-size:${value.fontSize}px` : "",
    value.minHeight != null ? `min-height:${value.minHeight}px` : "",
    value.align ? `text-align:${value.align}` : "",
  ].filter(Boolean).join(";");
  return <div id={id} style={{ backgroundColor: base.background || undefined, borderRadius: base.radius == null ? undefined : base.radius, maxWidth: base.maxWidth || undefined, marginInline: "auto", paddingTop: base.paddingTop, paddingBottom: base.paddingBottom, minHeight: base.minHeight, textAlign: base.align || undefined }}>
    <style>{`#${id}{${css(rules[0])}}@media(max-width:1279px){#${id}{${css(rules[1])}}}@media(max-width:767px){#${id}{${css(rules[2])}}}`}</style>
    {children}
  </div>;
}

export default function BlockRenderer({ blocks }) {
  if (!Array.isArray(blocks) || blocks.length === 0) return null;
  return (
    <div className="space-y-6" data-testid="block-renderer">
      {blocks
        .filter((b) => !b.hidden)
        .map((b) => {
          const rendered = (() => {
          switch (b.type) {
            case "heading":
              return b.level === "h3" ? (
                <h3
                  key={b.id}
                  className="font-display text-xl font-semibold text-dir-text"
                >
                  {b.text}
                </h3>
              ) : (
                <h2
                  key={b.id}
                  className="font-display text-2xl font-bold text-dir-text"
                >
                  {b.text}
                </h2>
              );
            case "paragraph":
              return (
                <p
                  key={b.id}
                  className="text-[15px] leading-relaxed text-dir-muted"
                >
                  {b.text}
                </p>
              );
            case "image":
              return (
                <figure key={b.id}>
                  <img
                    src={b.url}
                    alt={b.caption || ""}
                    className="w-full rounded-2xl border border-dir-gold/15 object-cover"
                  />
                  {b.caption && (
                    <figcaption className="mt-2 text-center text-xs text-dir-faint">
                      {b.caption}
                    </figcaption>
                  )}
                </figure>
              );
            case "gallery": {
              const urls = (b.urls || "")
                .split("\n")
                .map((u) => u.trim())
                .filter(Boolean);
              return (
                <div
                  key={b.id}
                  className="grid grid-cols-2 gap-3 sm:grid-cols-3"
                >
                  {urls.map((u, i) => (
                    <img
                      key={i}
                      src={u}
                      alt=""
                      className="h-40 w-full rounded-xl border border-dir-gold/15 object-cover"
                    />
                  ))}
                </div>
              );
            }
            case "quote":
              return (
                <blockquote
                  key={b.id}
                  className="border-l-2 border-dir-gold pl-6"
                >
                  <p className="font-display text-lg italic text-dir-text">
                    "{b.text}"
                  </p>
                  {b.author && (
                    <cite className="mt-2 block text-sm not-italic text-dir-gold">
                      — {b.author}
                    </cite>
                  )}
                </blockquote>
              );
            case "list": {
              const items = (b.items || "")
                .split("\n")
                .map((i) => i.trim())
                .filter(Boolean);
              const cls = "space-y-2 pl-6 text-[15px] text-dir-muted";
              return b.style === "number" ? (
                <ol key={b.id} className={`list-decimal ${cls}`}>
                  {items.map((it, i) => (
                    <li key={i}>{it}</li>
                  ))}
                </ol>
              ) : (
                <ul key={b.id} className={`list-disc ${cls}`}>
                  {items.map((it, i) => (
                    <li key={i}>{it}</li>
                  ))}
                </ul>
              );
            }
            case "button":
              if (b.visible === false) return null;
              return (
                <div key={b.id}>
                  <a
                    href={buttonUrl(b)}
                    target={b.newTab || b.link?.newTab ? "_blank" : undefined}
                    rel={b.newTab || b.link?.newTab ? "noopener noreferrer" : undefined}
                    className={buttonClass(b)}
                  >
                    {b.text || "Learn More"}
                  </a>
                </div>
              );
            case "video": {
              const src = toEmbed(b.url);
              return src ? (
                <div
                  key={b.id}
                  className="aspect-video overflow-hidden rounded-2xl border border-dir-gold/15"
                >
                  <iframe
                    src={src}
                    title="video"
                    className="h-full w-full"
                    allowFullScreen
                  />
                </div>
              ) : null;
            }
            case "divider":
              return <hr key={b.id} className="border-dir-gold/15" />;
            case "hero":
              return (
                <section
                  key={b.id}
                  className="relative overflow-hidden rounded-2xl border border-dir-gold/20 bg-dir-deep p-8 sm:p-12"
                >
                  {b.image && (
                    <img
                      src={b.image}
                      alt=""
                      className="absolute inset-0 h-full w-full object-cover opacity-20"
                    />
                  )}
                  <div className="relative">
                    <h2 className="text-3xl font-bold text-dir-text">
                      {b.title}
                    </h2>
                    <p className="mt-3 max-w-2xl text-dir-muted">{b.text}</p>
                    {b.buttonText && (
                      <a
                        href={safeUrl(b.url)}
                        className="mt-6 inline-block rounded-full bg-dir-gold px-5 py-2.5 font-bold text-dir-deep"
                      >
                        {b.buttonText}
                      </a>
                    )}
                  </div>
                </section>
              );
            case "rich_text":
              return (
                <div
                  key={b.id}
                  className="whitespace-pre-line text-[15px] leading-7 text-dir-muted"
                >
                  {b.text}
                </div>
              );
            case "image_text":
              return (
                <section
                  key={b.id}
                  className="grid items-center gap-6 rounded-2xl border border-dir-gold/15 bg-dir-card p-6 md:grid-cols-2"
                >
                  {b.image && (
                    <img
                      src={b.image}
                      alt={b.title || ""}
                      className="h-64 w-full rounded-xl object-cover"
                    />
                  )}
                  <div>
                    <h2 className="text-2xl font-bold text-dir-text">
                      {b.title}
                    </h2>
                    <p className="mt-3 whitespace-pre-line text-dir-muted">
                      {b.text}
                    </p>
                  </div>
                </section>
              );
            case "cta":
              return (
                <section
                  key={b.id}
                  className="rounded-2xl bg-dir-green p-8 text-center"
                >
                  <h2 className="text-2xl font-bold text-dir-text">
                    {b.title}
                  </h2>
                  <p className="mx-auto mt-3 max-w-2xl text-dir-muted">
                    {b.text}
                  </p>
                  {b.buttonText && (
                    <a
                      href={safeUrl(b.url)}
                      className="mt-5 inline-block rounded-full bg-dir-gold px-5 py-2.5 font-bold text-dir-deep"
                    >
                      {b.buttonText}
                    </a>
                  )}
                </section>
              );
            case "statistics":
            case "feature_cards":
            case "two_column":
            case "three_column": {
              const entries = (b.items || "")
                .split("\n")
                .filter(Boolean)
                .map((line) => {
                  const [title, ...rest] = line.split("|");
                  return { title: title.trim(), text: rest.join("|").trim() };
                });
              const cols =
                b.type === "two_column"
                  ? "md:grid-cols-2"
                  : b.type === "three_column"
                    ? "md:grid-cols-3"
                    : "sm:grid-cols-2 lg:grid-cols-4";
              return (
                <div key={b.id} className={`grid gap-4 ${cols}`}>
                  {entries.map((x, i) => (
                    <div
                      key={i}
                      className="rounded-xl border border-dir-gold/15 bg-dir-card p-5"
                    >
                      <div
                        className={
                          b.type === "statistics"
                            ? "text-3xl font-bold text-dir-gold"
                            : "text-lg font-semibold text-dir-text"
                        }
                      >
                        {x.title}
                      </div>
                      {x.text && (
                        <p className="mt-2 text-sm text-dir-muted">{x.text}</p>
                      )}
                    </div>
                  ))}
                </div>
              );
            }
            case "testimonials":
              return (
                <blockquote
                  key={b.id}
                  className="rounded-2xl border border-dir-gold/20 bg-dir-card p-8 text-center"
                >
                  <p className="text-xl italic text-dir-text">“{b.text}”</p>
                  {b.author && (
                    <cite className="mt-4 block not-italic text-dir-gold">
                      — {b.author}
                    </cite>
                  )}
                </blockquote>
              );
            case "spacer":
              return (
                <div
                  key={b.id}
                  aria-hidden="true"
                  style={{
                    height: Math.min(240, Math.max(8, Number(b.size) || 48)),
                  }}
                />
              );
            case "featured_article":
            case "featured_news":
            case "featured_event":
            case "featured_opportunity": {
              const base =
                b.type === "featured_article"
                  ? "/articles"
                  : b.type === "featured_news"
                    ? "/news"
                    : b.type === "featured_event"
                      ? "/events"
                      : "/opportunities";
              return (
                <a
                  key={b.id}
                  href={`${base}/${b.slug}`}
                  className="block rounded-2xl border border-dir-gold/20 bg-dir-card p-6 text-lg font-semibold text-dir-text hover:border-dir-gold"
                >
                  View featured {b.type.replace("featured_", " ")} →
                </a>
              );
            }
            case "map":
              return (
                <section
                  key={b.id}
                  className="rounded-2xl border border-dir-gold/20 bg-dir-deep p-8 text-center"
                >
                  <h2 className="text-2xl font-bold text-dir-text">
                    {b.title}
                  </h2>
                  <p className="mx-auto mt-3 max-w-2xl text-dir-muted">
                    {b.text}
                  </p>
                  <a
                    href="/global-network"
                    className="mt-5 inline-block text-dir-gold"
                  >
                    Explore the global network →
                  </a>
                </section>
              );
            case "newsletter":
              return (
                <section
                  key={b.id}
                  className="rounded-2xl border border-dir-gold/20 bg-dir-card p-8 text-center"
                >
                  <h2 className="text-2xl font-bold text-dir-text">
                    {b.title}
                  </h2>
                  <p className="mt-3 text-dir-muted">{b.text}</p>
                  <a
                    href="#newsletter"
                    className="mt-5 inline-block rounded-full bg-dir-gold px-5 py-2.5 font-bold text-dir-deep"
                  >
                    {b.buttonText || "Subscribe"}
                  </a>
                </section>
              );
            case "dynamic_collection":
              return <DynamicCollection key={b.id} block={b} />;
            default:
              return null;
          }
          })();
          return rendered ? <ResponsiveBlock key={b.id} block={b}>{rendered}</ResponsiveBlock> : null;
        })}
    </div>
  );
}

function DynamicCollection({ block }) {
  const [items, setItems] = useState(null);
  useEffect(() => {
    api
      .get(`/${block.source || "news"}`, {
        params: {
          limit: block.limit || 3,
          category: block.category || undefined,
        },
      })
      .then(({ data }) => {
        let rows = Array.isArray(data) ? data : [];
        if (block.featuredOnly) rows = rows.filter((x) => x.featured);
        if (block.sort === "oldest") rows = [...rows].reverse();
        if (block.sort === "title")
          rows = [...rows].sort((a, b) =>
            (a.title || a.name || "").localeCompare(b.title || b.name || ""),
          );
        setItems(rows.slice(0, block.limit || 3));
      })
      .catch(() => setItems([]));
  }, [
    block.source,
    block.limit,
    block.category,
    block.featuredOnly,
    block.sort,
  ]);
  return (
    <section>
      <h2 className="text-2xl font-bold text-dir-text">{block.title}</h2>
      {block.text && <p className="mt-2 text-dir-muted">{block.text}</p>}
      <div
        className={`mt-6 ${block.layout === "list" ? "space-y-3" : "grid gap-4 sm:grid-cols-2 lg:grid-cols-3"}`}
      >
        {items === null ? (
          <p className="text-dir-muted">Loading…</p>
        ) : (
          items.map((x) => (
            <a
              key={x.id}
              href={x.slug ? `/${block.source}/${x.slug}` : x.url || "#"}
              className="block rounded-2xl border border-dir-gold/15 bg-dir-card p-5 transition hover:border-dir-gold"
            >
              <h3 className="font-semibold text-dir-text">
                {x.title || x.headline || x.name}
              </h3>
              <p className="mt-2 line-clamp-3 text-sm text-dir-muted">
                {x.excerpt || x.description}
              </p>
            </a>
          ))
        )}
        {items?.length === 0 && (
          <p className="text-dir-muted">No published items found.</p>
        )}
      </div>
    </section>
  );
}
